import { useMemo, useState } from "react";
import { Download, FileSpreadsheet, FileText, Info } from "lucide-react";
import { downloadCsv, downloadReport, downloadReportXlsx } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import "@/components/EngineReport.css";

const BOOKING_DETAIL_COLUMNS = ["Project Name", "Client Name", "Booking Date", "WING", "Flat No", "Config", "Contact Number", "Carpet", "AV", "Amount Due"];

function BookingDetailsDialog({ details, onClose }) {
  return (
    <Dialog open={Boolean(details)} onOpenChange={open => !open && onClose()}>
      <DialogContent className="booking-detail-dialog sm:max-w-5xl">
        <DialogHeader><DialogTitle>{details?.title || "Booking details"}</DialogTitle></DialogHeader>
        {details?.rows.length ? (
          <div className="booking-detail-scroll">
            <table className="booking-detail-table">
              <thead><tr>{BOOKING_DETAIL_COLUMNS.map(column => <th key={column}>{column}</th>)}</tr></thead>
              <tbody>{details.rows.map((row, index) => (
                <tr key={`${row["Project Name"]}-${row["Flat No"]}-${index}`}>
                  {BOOKING_DETAIL_COLUMNS.map(column => <td key={column}>{row[column] ?? "—"}</td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="booking-detail-empty">No booking records contribute to this value.</p>}
      </DialogContent>
    </Dialog>
  );
}

const COUNT_OPS = new Set(["count_rows", "count_distinct"]);

function RecordsDialog({ details, onClose }) {
  const columns = useMemo(() => {
    const seen = [];
    (details?.rows || []).forEach(row => Object.keys(row).forEach(key => { if (!seen.includes(key)) seen.push(key); }));
    return seen;
  }, [details]);
  return (
    <Dialog open={Boolean(details)} onOpenChange={open => !open && onClose()}>
      <DialogContent className="booking-detail-dialog sm:max-w-5xl">
        <DialogHeader><DialogTitle>{details?.title || "Details"}</DialogTitle></DialogHeader>
        <div className="booking-detail-scroll">
          <table className="booking-detail-table">
            <thead><tr>{columns.map(column => <th key={column}>{column}</th>)}</tr></thead>
            <tbody>{(details?.rows || []).map((row, index) => (
              <tr key={index}>{columns.map(column => <td key={column}>{String(row[column] ?? "") || "—"}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KpiGrid({ kpis, records, onOpen }) {
  if (!kpis.length) return null;
  return (
    <div className="kpi-grid" data-testid="report-kpis">
      {kpis.map(k => (
        <div className="kpi" key={k.label} data-testid={`kpi-${k.label.replace(/\W+/g, "-").toLowerCase()}`}>
          <span>{k.label}</span>
          {COUNT_OPS.has(k.operation) && records?.length
            ? <strong><button type="button" className="engine-report-cell-trigger" title="View details" onClick={() => onOpen({ title: k.label, rows: records })}>{k.value}</button></strong>
            : <strong>{k.value}</strong>}
        </div>
      ))}
    </div>
  );
}

function DetailTable({ rows, kpis, detailRecords, onOpen }) {
  const columns = useMemo(() => Object.keys(rows[0] || {}), [rows]);
  if (!rows.length) return null;
  const countLabels = new Set((kpis || []).filter(k => COUNT_OPS.has(k.operation)).map(k => k.label));
  return (
    <div className="table-scroll">
      <table data-testid="report-table">
        <thead><tr>{columns.map(k => <th key={k}>{k}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row[columns[0]]}-${i}`}>{columns.map((k, j) => {
              const records = detailRecords?.[i];
              if (countLabels.has(k) && records?.length) {
                return <td key={k}><button type="button" className="engine-report-cell-trigger" title="View details" onClick={() => onOpen({ title: `${row[columns[0]]} · ${k}`, rows: records })}>{row[k]}</button></td>;
              }
              return <td key={k} className={j === 0 ? "strong-cell" : ""}>{row[k]}</td>;
            })}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReportCard({ report }) {
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [recordDetails, setRecordDetails] = useState(null);
  const timestamp = report.last_synced_at || report.data_timestamp;
  const updated = useMemo(() => timestamp ? new Date(timestamp).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "N/A", [timestamp]);
  if (report.columns && report.rows) {
    const tableRows = report.total_row ? [...report.rows, report.total_row] : report.rows;
    const ranges = Object.entries(report.ranges_used || {}).map(([key, value]) => `${key.replaceAll("_", " ")}: ${value}`).join(" | ");
    return (
      <article className="report-card engine-report-card" data-testid="report-result">
        <div className="report-header">
          <div><div className="report-kicker"><FileText size={14} /> {report.report_type}</div></div>
          <button className="export-btn" data-testid="export-xlsx-button" onClick={() => downloadReportXlsx(report)}><FileSpreadsheet size={15} /> Excel</button>
        </div>
        {report.warnings?.length > 0 && <div className="engine-report-warning" role="alert" data-testid="report-warnings"><Info size={16} /><ul>{report.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}</ul></div>}
        <div className="table-scroll">
          <table className="engine-report-table" data-testid="report-table">
            <thead><tr>{report.columns.map(column => <th key={column.key}>{column.label}</th>)}</tr></thead>
            <tbody>
              {tableRows.map((row, index) => (
                <tr className={row === report.total_row ? "engine-report-total" : ""} key={`${row["Project Name"]}-${index}`}>
                  {report.columns.map(column => {
                    const value = row[column.label] ?? "";
                    if (column.unit === "text" || typeof value !== "number" || !Number.isFinite(value)) {
                      return <td key={column.key}>{value}</td>;
                    }
                    const isTotal = row === report.total_row;
                    const projectId = report.project_ids?.[index];
                    const details = isTotal
                      ? Object.entries(report.drilldowns || {}).flatMap(([id, metrics]) =>
                        (metrics[column.key] || []).map(detailIndex => report.booking_details?.[id]?.[detailIndex]).filter(Boolean))
                      : (report.drilldowns?.[projectId]?.[column.key] || [])
                        .map(detailIndex => report.booking_details?.[projectId]?.[detailIndex]).filter(Boolean);
                    return (
                      <td key={column.key}>
                        <button
                          type="button"
                          className="engine-report-cell-trigger"
                          title="View contributing booking details"
                          onClick={() => setSelectedDetails({ title: `${row["Project Name"]} · ${column.label}`, rows: details })}
                        >{value}</button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {report.definitions?.length > 0 && <ul className="definitions" data-testid="report-definitions">{report.definitions.map((definition, index) => <li key={`${definition}-${index}`}>{definition}</li>)}</ul>}
        <div className="engine-report-footer" data-testid="report-footer">Based on {report.booking_count} bookings | Data as of {report.last_synced_at || "N/A"} | Ranges: {ranges || "N/A"}</div>
        <BookingDetailsDialog details={selectedDetails} onClose={() => setSelectedDetails(null)} />
      </article>
    );
  }
  if (report.display_mode === "answer") {
    return (
      <article className="report-card answer-card" data-testid="report-result">
        <div className="report-header"><div><div className="report-kicker"><FileText size={14} /> Sheet data answer</div><h2 data-testid="report-summary">{report.summary}</h2></div></div>
        <div className="report-meta"><span><i className="dot-blue"></i> {report.projects.join(" · ")}</span><span>Data synced {updated}</span></div>
        {report.definitions?.length > 0 && <ul className="definitions" data-testid="report-definitions">{report.definitions.map(d => <li key={d}>{d}</li>)}</ul>}
        <div className="report-footer"><span><span className="verified-mark">✓</span> Answer grounded in synced sheet rows</span><span className="ai-note" data-testid="report-ai-status">{report.ai_status}</span></div>
      </article>
    );
  }
  return (
    <article className={`report-card ${report.available === false ? "report-unavailable" : ""}`} data-testid="report-result">
      <div className="report-header">
        <div><div className="report-kicker"><FileText size={14} /> {report.report_type}</div><h2 data-testid="report-summary">{report.summary}</h2></div>
        {report.available !== false && (
          <div className="export-group">
            <button className="export-btn" data-testid="export-csv-button" onClick={() => downloadCsv(report)}><FileSpreadsheet size={15} /> CSV</button>
            <button className="export-btn" data-testid="export-report-button" onClick={() => downloadReport(report)}><Download size={15} /> JSON</button>
          </div>
        )}
      </div>
      <div className="report-meta"><span><i className="dot-blue"></i> {report.projects.join(" · ")}</span><span>Data synced {updated}</span></div>
      {report.available === false
        ? <div className="unavailable-note" data-testid="report-unavailable"><Info size={15} /> Connect a Google Sheet for {report.missing_projects?.join(", ") || "this project"} in Data sources to generate this report.</div>
        : <>
          <KpiGrid kpis={report.kpis || []} records={report.kpi_records} onOpen={setRecordDetails} />
          <DetailTable rows={report.details || []} kpis={report.kpis} detailRecords={report.detail_records} onOpen={setRecordDetails} />
          {report.definitions?.length > 0 && <ul className="definitions" data-testid="report-definitions">{report.definitions.map(d => <li key={d}>{d}</li>)}</ul>}
        </>}
      <div className="report-footer"><span><span className="verified-mark">✓</span> Figures computed from synced sheet rows{report.cache_hit ? " · cached" : ""}</span><span className="ai-note" data-testid="report-ai-status">{report.ai_status}</span></div>
      <RecordsDialog details={recordDetails} onClose={() => setRecordDetails(null)} />
    </article>
  );
}
